import { Icon, useModal, DateField } from '@jutro/components';
import { BreakpointTrackerContext } from '@jutro/layout';
import { useAuthentication } from '@xengage/gw-digital-auth-react';
import { messages as commonMessages } from '@xengage/gw-platform-translations';
import { useDataRefresh, ViewModelForm, ViewModelServiceContext } from '@xengage/gw-portals-viewmodel-react';
import { SubmissionService, ActivitiesService } from 'gw-capability-gateway';
import { ENTGatewayDocumentService } from 'ent-capability-gateway-document';
import _ from 'lodash';
import React, { useCallback, useContext, useState, useEffect, useMemo } from 'react';
import { TranslatorContext } from '@jutro/locale';
import { GatewayRenewalService } from 'twia-capability-gateway-policyrenewal';
import { InputField, DropdownSelectField } from '@jutro/legacy/components';
import { Link } from '@jutro/router';
import { CpCoverablesService } from 'twia-capability-quoteandbind-cp';
import messages from './ENTDocumentUploadComponent.messages.js';
import metadata from './ENTDocumentUploadComponent.metadata.json5';
import styles from './ENTDocumentUploadComponent.module.scss';



function ENTDocumentUploadComponent(props) {
    const breakpoint = useContext(BreakpointTrackerContext);
    const { showAlert } = useModal();
    const { policyNumber, productCode, jobNumber, uwCompany, activityPublicID, locations, jobType, skipReviewActivity, disableUpload } = props;
    const authHeader = _.get(useAuthentication(), 'authHeader');
    const { refreshData } = useDataRefresh();
    const [documentSearch, setDocumentSearch] = useState('');
    const [documentTypes, setDocumentTypes] = useState([]);
    const [documentType, setDocumentType] = useState();
    const [documents, setDocuments] = useState([]);
    const [displayDocuments, setDisplayDocuments] = useState([]);
    const [loading, setLoading] = useState(false);
    const [buildings, setBuildings] = useState([]);
    const isTFPA = uwCompany === 'tfpa';
    const isTWIA = uwCompany === 'twia';
    const isResidential = productCode === 'Residential';
    const isCommercial = productCode === 'Commercial';
    const viewModelService = useContext(ViewModelServiceContext);
    const translator = useContext(TranslatorContext);
    // DE16847 add NA option to building list
    const buildingOptionNA = {
        code: 0,
        name: 'N/A'
    };

    const getDocumentTypeFilterName = useCallback(() => {
        if (isTFPA) {
            return 'TFPAPortalResPolicyUploadDocTypesFilter_Ext';
        }

        if (policyNumber) {
            if (isTWIA && isResidential) {
                return 'TwiaPortalResPolicyUploadDocTypesFilter_Ext';
            } else if (isTWIA && isCommercial) {
                return 'TwiaPortalComPolicyUploadDocTypesFilter_Ext';
            }
        } else if (jobNumber) {
            if (isTWIA && isResidential) {
                return 'TwiaPortalResJobUploadDocTypesFilter_Ext';
            } else if (isTWIA && isCommercial) {
                return 'TwiaPortalComJobUploadDocTypesFilter_Ext';
            }
        }

        return 'TwiaUploadDocTypesFilter_Ext';
    }, [isCommercial, isResidential, isTFPA, isTWIA, jobNumber, policyNumber]);

    const documentTypeCodes = viewModelService.productMetadata.get('pc').types.getTypelist('typekey.DocumentType');
    const filteredDocumentTypes = documentTypeCodes.filters.find(f => f.name === getDocumentTypeFilterName()).codes;

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const loadDocuments = useCallback(async () => {
        try {
            setLoading(true);

            let docs;

            if (activityPublicID) {  // Can add additional functions for policy, account
                docs = await ActivitiesService.getDocumentsForActivity_TWIA(activityPublicID, authHeader)
            } else if(jobType === 'Renewal') {
                docs = await GatewayRenewalService.getDocumentsForRenewal(jobNumber, authHeader)
            } else {
                docs = await SubmissionService.getDocumentsForSubmission(jobNumber, authHeader)
            }

            setDocuments(docs);
            setDisplayDocuments(docs);

        } catch (e) {
            showAlert({
                title: commonMessages.errorUploadTitle,
                message: messages.unableToDownload,
                status: 'error',
                icon: 'mi-error-outline',
                confirmButtonText: commonMessages.ok,
            })
        } finally {
            setLoading(false);
        }

    });

    const writeDocumentValue = useCallback((index, path, value) => {
        const doc = _.cloneDeep(displayDocuments[index]);

        _.set(doc, path, value);
        doc.edited = true;

        const updatedDocuments = _.cloneDeep(displayDocuments);

        updatedDocuments[index] = doc;
        setDocuments(updatedDocuments);
        setDisplayDocuments(updatedDocuments);
        refreshData();
    }, [displayDocuments, setDocuments, setDisplayDocuments, refreshData]);

    const getDocumentBuilding = useCallback((item, index) => {
        if(isCommercial && policyNumber && !jobNumber) {
            return 'N/A';
        }

        return (
            isEditable(item) ?
                <DropdownSelectField
                    id={`building${index}`}
                    alwaysShowPlaceholder={false}
                    availableValues={buildings}
                    path="buildingFixedID_TWIA"
                    onValueChange={(value) => { writeDocumentValue(index, 'buildingFixedID_TWIA', value) }}
                    value={item.buildingFixedID_TWIA}
                /> : item.buildingDisplay_TWIA
        )
    }, [buildings, isCommercial, jobNumber, policyNumber, writeDocumentValue]);

    useEffect(() => {

        async function fetchData() {

        const formattedDocumentTypeCodes = filteredDocumentTypes?.map(code => ({
            code: code.code,
            name: translator({ id: code.name })
        }));

        const mappedDocTypeValues = formattedDocumentTypeCodes && formattedDocumentTypeCodes.map(docType => {
            const name = docType.code === 'other' ? messages.otherDocument : { id: docType.name, defaultMessage: docType.name };

            return {
                code: docType.code,
                name: translator(name),
            };
        });


        let response = null;        

        
            if (isCommercial) {   
            setLoading(true);

            try {
                // First check if jobNumber is available
                if (jobNumber) {
                    response = await CpCoverablesService.getLocationsAndBuildingsForJob_TWIA(jobNumber, authHeader);
                }

                // If jobNumber isn't available but policyNumber is, then try to fetch by policyNumber
                if (!response && policyNumber) {
                    response = await CpCoverablesService.getLocationsAndBuildingsForPolicy_TWIA(policyNumber, null, authHeader);
                }
            } catch (error) {
                setLoading(false);
                response = null; // Optionally handle error response
            }

            setLoading(false);
        }


        const locationMap = response && new Map(response.map(l => [l.publicID, l]));
        let mappedBuildings = response && response.flatMap(l => l.buildings);
        
        mappedBuildings = mappedBuildings && mappedBuildings.map(building => {
             // DE16862 fix building description
             const loc = locationMap && locationMap.get(building.locationId);
             const desc = _.isNil(building.description) ? '' : building.description;
             const name = `${building.buildingNumber}: ${desc} (${loc.displayName})`;

             return {
                 code: building.fixedID,
                 name
             };
        });
        // DE16847 add NA option to building list
        
        if (isCommercial) {
            mappedBuildings?.unshift(buildingOptionNA);
        }

        setBuildings(mappedBuildings);
        setDocumentTypes(mappedDocTypeValues);
        setDocumentSearch('');
        loadDocuments();
    }

    fetchData();
    }, []);

    const onUploadDocument = useCallback(item => {
        let entityName;

        if (activityPublicID) { // update appropriate entity type like Job or Policy
            entityName = 'entity.Activity';
        }

        const documentMetaDataTemplate = {
            docUID: '001',
            documentType,
            securityType: 'unrestricted',
            status: 'approved',
            jobNumber,
            policyNumber,
            name: item.name,
            linkedEntityType_TWIA: entityName,
            linkedEntityPublicID_TWIA: activityPublicID, // Entity PublicID to be assiciated with  
            // 2025-05-22 - Debra Frum - DE19212 - TWIA Underwriting Response activities are triggering under open transactions
            //   Functionality for TWIA didn't get implemented when TFPA & TWIA portals merged during move to Guidewire Cloud
            // 2022-05-24 - Debra Frum - US22167 - PC / PE Underwriting Response Activity
            //   When true, a Review activity won't be generated when a document is uploaded
            skipReviewActivity_TWIA: skipReviewActivity,
            sessionID: '-'
        };

        setLoading(true);

        ENTGatewayDocumentService.uploadDocument(
            item,
            documentMetaDataTemplate,
            authHeader
        )
            .then(loadDocuments)
            .catch(() => {
                showAlert({
                    title: commonMessages.errorUploadTitle,
                    message: commonMessages.uploadFailedMessage,
                    status: 'error',
                    icon: 'mi-error-outline',
                    confirmButtonText: commonMessages.ok,
                }).catch(_.noop);
            })
            .finally(() => {
                setLoading(false);
            });
    }, [activityPublicID, documentType, jobNumber, policyNumber, authHeader, loadDocuments, showAlert]);

    const onChangeDocumentType = useCallback(
        docType => {
            setDocumentType(docType);
        },
        [setDocumentType]
    );

    const onChangeDocumentSearch = useCallback(
        searchTerm => {
            if (searchTerm && documents && documents.length > 0) {
                const filtered = documents?.filter(doc => doc.name?.toLowerCase().includes(searchTerm?.toLowerCase()));

                setDisplayDocuments(filtered);
            } else {
                setDisplayDocuments(documents);
            }

            setDocumentSearch(searchTerm);
        },
        [setDocumentSearch, setDisplayDocuments, documents]
    );

    const onDocumentUpdate = useCallback(item => {
        if (isCommercial && item.buildingFixedID_TWIA === undefined && !policyNumber && jobNumber) {
            showAlert({
                title: commonMessages.errorUploadTitle,
                message: messages.noBuildingSelected,
                status: 'error',
                icon: 'mi-error-outline',
                confirmButtonText: commonMessages.ok,
            }).catch(_.noop);
        } else {
            setLoading(true);
            ENTGatewayDocumentService.updateDocument(
                item,
                authHeader
            )
                .then(result => {
                    item.buildingDisplay_TWIA = _.get(result, 'buildingDisplay_TWIA');
                    item.edited = false;
                })
                .catch(() => {
                    showAlert({
                        title: commonMessages.errorUploadTitle,
                        message: commonMessages.uploadFailedMessage,
                        status: 'error',
                        icon: 'mi-error-outline',
                        confirmButtonText: commonMessages.ok,
                    }).catch(_.noop);
                })
                .finally(() => {
                    setLoading(false);
                });
        }
    }, [isCommercial, policyNumber, jobNumber, showAlert, authHeader]);

    const onDownloadDocument = useCallback(async item => {
        setLoading(true);

        try {
            const sessionID = await ENTGatewayDocumentService.generateDownloadToken(item, authHeader);
            const downloadURL = ENTGatewayDocumentService.downloadDocument(item.docUID, sessionID);

            window.location.href = `${downloadURL}&job=${jobNumber}`;
        } catch (error) {
            showAlert({
                title: commonMessages.errorUploadTitle,
                message: commonMessages.errorGenerateUploadToken,
                status: 'error',
                icon: 'mi-error-outline',
                confirmButtonText: commonMessages.ok,
            })
                .catch(_.noop);
        } finally {
            setLoading(false);
        }
    }, [authHeader, showAlert]);

    const getDocumentName = useCallback((item, index) => (
        <Link style={{ justifyItems: 'left' }} onClick={() => { onDownloadDocument(item) }}>{item.name}</Link>
    ), [onDownloadDocument]);

    const isEditable = (item) => _.isNil(item.description) || item.edited;

    const getDocumentType = (item) => {
        const code = filteredDocumentTypes?.find(val => val.code == item.documentType);

        return code ? translator({ id: code.name }) : item.documentType;
    };

    const getDocumentDescription = useCallback((item, index) => (
            isEditable(item) ?
                <InputField
                    id={`description${index}`}
                    path="description"
                    onValueChange={(value) => { writeDocumentValue(index, 'description', value) }}
                    value={item.description}
                    required
                    showRequired
                /> : item.description
        ), [writeDocumentValue]);

    const getDocumentUploaded = useCallback((item, index) => (
        <DateField
            id={`uploadedDate${index}`}
            value={item.dateModified}
            readOnly
            displayFormat="vshort"
        />
    ), []);

    const getDocumentUpdated = useCallback((item, index) => (
        isEditable(item) ?
            <Icon
                tag="span"
                id={`update${index}`}
                onClick={() => onDocumentUpdate(item)}
                size="small"
                icon="mi-save"
                color="#26a9e0"
            /> : <div></div>
    ), []);

    const getContentForUploadInstructions = useCallback(() => {
        if (isTFPA) {
            return translator(messages.residentialTFPA)
        } else if (isTWIA && isCommercial) {
            return translator(messages.commercialTWIA)
        } else if (isTWIA && isResidential) {
            return translator(messages.residentialTWIA)
        }
    }, [])



    const overrideProps = {
        '@field': {
            labelPosition: breakpoint === 'desktop' ? 'left' : 'top',
        },
        documentUploadContainer: {
            visible: !loading
        },
        loadingIndicator: {
            visible: loading
        },
        uploadDocumentType: {
            availableValues: documentTypes,
            value: documentType,
            disabled: disableUpload
        },
        uploadDocument: {
            disabled: documentType === undefined || disableUpload,
        },
        documentSearch: {
            visible: documents && documents.length > 0,
            value: documentSearch
        },
        documentsTable: {
            data: displayDocuments
        },
        uploadInstructions: {
            content: getContentForUploadInstructions()
        },
        activityTitle: {
            visible: activityPublicID !== undefined && activityPublicID
        },
        buildingColumn: {
            visible: isCommercial
        },
    };


    const resolvers = {
        resolveClassNameMap: styles,
        resolveComponentMap: {},
        resolveCallbackMap: {
            onUploadDocument,
            onChangeDocumentType,
            onChangeDocumentSearch,
            getDocumentName,
            getDocumentDescription,
            getDocumentType,
            getDocumentUploaded,
            getDocumentUpdated,
            getDocumentBuilding
        },
    };

    return (
        <ViewModelForm
            uiProps={metadata.pageContent}
            overrideProps={overrideProps}
            callbackMap={resolvers.resolveCallbackMap}
            componentMap={resolvers.resolveComponentMap}
            classNameMap={resolvers.resolveClassNameMap}
        />
    );
}

export default ENTDocumentUploadComponent;
